import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  setTranscriptData,
  appendTranscriptData,
  toggleStrike,
  changeSpeaker,
  rotateSpeaker,
  split,
  merge,
  replaceInSummary,
  replaceInChapters,
  simpleFind,
  complexFind,
  applySimpleReplace,
  applyComplexReplace,
} from "./transcripts";
import {
  getWorkflowTranscript,
  getPaginatedWorkflowTranscript,
  saveWorkflow,
  updateSpeakers,
  addComment,
  deleteComment,
  completeWorkflow,
  exportTranscript,
  updateComment,
  addChats,
  validateTranscriptEntities,
  triggerSpeakerAttributionCheck,
  getAcceptedMembers,
  getTranscriptVersion,
  getTranscriptBackupVersions,
} from "../../utils/enterpriseApi";
import { showToast } from "../toastSlice";
import { getNextStatusFromWorkflow } from "pages/editTranscript/config/projectPipelines";
import { ColorMap } from "../../utils/colorMaps";
import { download } from "../../pages/editTranscript/util/player";
import {
  initializeFromProcessedSpeakers,
  applySavedFormattedSpeakers,
} from "../speakerSlice";
import {
  getPartnerSpeakerConfig,
  usesGenericSpeakers,
  buildGenericSpeakerLabels,
  remapGenericParagraphSpeakers,
} from "DAIEditor/config/speakerSettings";
import { MAX_AI_QC_ATTEMPTS, MAX_SPEAKER_ATTRIBUTION_ATTEMPTS } from "pages/editTranscript/navbar/navButtons";
import { serialize } from "pages/editTranscript/util/diffUtils";
import {
  availableDiffVersionKeys,
  getDiffHopKeys,
  getIntermediateVersionKeys,
  versionKeysFromBackups,
  missingRequiredVersionKeys,
  missingSnapshotMessage,
} from "pages/editTranscript/util/diffActors";
import {
  emitTranscriptAppend,
  emitTranscriptForceReplace,
  storeAppendedTail,
} from "pages/editTranscript/util/slateTranscriptSync";
import {
  TRANSCRIPT_PAGE_SIZE,
  TRANSCRIPT_PAGE_CAP_MESSAGE,
  appendLiveStorePage,
  canFetchNewTranscripts,
  concatTranscriptPages,
  fetchRemainingTranscriptPages,
  nextTranscriptPageStart,
  serverTranscriptCount,
  shouldApplyInFlightTranscript,
} from "pages/editTranscript/util/transcriptPages";
import { reloadTranscriptAfterConflict } from "pages/editTranscript/util/conflictReload";
import { isDeltaUpdatesEnabledForWorkflow } from "pages/editTranscript/util/deltaUpdatesGate";
import {
  injectRandomSnippets,
  stripRedHerringWords,
  loadRedHerringState,
  saveRedHerringState,
  snapshotRedHerringState,
  restoreRedHerringState,
} from "pages/editTranscript/util/randomNoise";

// Transcript validation maxed at 341s over 2 days of prod, so 360 left a 19s margin and these
// thunks re-POST on retrigger, burning one of the 5 attempts each time. Same failure the speaker
// path had. 900s is well clear of the observed tail and still terminates.
const AI_VALIDATION_RECHECK_TIME = 900;

const initialState = {
  workflowData: {},
  summary: "",
  contextSummary: "",
  transcripts: [],
  transcriptsInitialized: false,
  transcriptUpdates: [], // keeps track of updates to transcripts via find/replace
  speakers: [],
  speakerNames: [],
  entities: {},
  entitiesMap: {}, // map word indexes to entities, for highlighting purposes
  insights: [],
  keywords: [],
  accuracy: { bad: true, good: true, excellent: true },
  colorMap: {},
  comments: [],
  hasUnsavedChanges: false,
  hasUnsavedEntities: false,
  hasUnsavedInsights: false,
  hasUnsavedKeywords: false,
  isSaving: false,
  lastSavedAt: null,
  editorMode: null,
  highlightedText: null,
  highlightAnchor: null,
  isEntityHighlightDisabled: false,
  searchTerm: "",
  replaceTerm: "",
  isCaseSensitive: false,
  triggerManualSave: false,
  isReplaceLoading: false,
  isWorkflowSaving: false,
  isWorkflowSaved: false,
  isDownloading: false, // flag for export as PDF/docx/txt downloads
  isDownloaded: false,
  isHintsOpen: false, // editor shorcut hints
  isFocusMode: false, // focus mode state
  version: 0,
  versionConflict: false,
  acceptedMembers: [],
  baselineTranscripts: [], // baseline version for diff/delta comparison
  isTranscriptDiffModeOn: false, // flag for transcript diff mode
  isTranscriptDiffLoading: false, // flag for transcript diff loading between various versions
  sourceVersion: null, // source version for transcript diff
  targetVersion: null, // target version for transcript diff
  versionLabels: {
    source: null,
    target: null,
  }, // map of version to label
  transcriptSources: {
    ai_transcript: null,
    speaker_final: null,
    fd_final: null,
    human_final: null,
    current: null,
  }, // map of version to transcript data
  diffVersionKeys: {
    source: null,
    target: null,
  },
  wordCount: 0, // optimal word count recommended per paragraph
  aiQcLimit: 0,
  speakerValidationLimit: 0,
  speakerAttributionLimit: 0,
  redHerringIds: [],
};

const deserialize = (transcript) => {
  return transcript.map((t) => {
    const snippet = {
      type: "snippet",
      id: t.id,
      start_timestamp: t.start_timestamp,
      start: t.start,
      speaker: t.speaker,
      question_type: t.question_type ?? "",
      children: [
        {
          type: "paragraph",
          snippetId: t.id,
          children: t.words.map((word, index) => {
            return {
              ...word,
              id: index,
              snippetId: t.id,
            };
          }),
        },
      ],
    };

    return snippet;
  });
};

// Converts { x: [a, b, c], y: [d, e, f] } to { a: x, b: x, c: x, d: y, e: y, f: y }
const invertEntities = (entities) => {
  const entitiesMap = {};

  if (entities) {
    const keys = Object.keys(entities);

    keys.forEach((key) => {
      entities[key].forEach((item) => {
        entitiesMap[item] = key;
      });
    });
  }

  return entitiesMap;
};

const editorSlice = createSlice({
  name: "editor",
  initialState,

  reducers: {
    reset: () => initialState,

    initializeWorkflow: (state, action) => {
      state.workflowData = action.payload;
      state.summary = action.payload.processed_data.abstract;
      // Client-supplied event context. It is ground truth for the subject company and
      // key entities, so editors should see it above the generated summary.
      state.contextSummary = action.payload.processed_data.context_summary || "";
      state.insights = action.payload.processed_data.insights || [];
      state.keywords = action.payload.processed_data.keywords || [];
      state.wordCount = action.payload.paragraph_word_count || 0;

      const incomingSpeakers = action.payload.processed_data.speakers || [];

      // Generic partners (e.g. Third Bridge) display anonymized positional labels.
      if (usesGenericSpeakers(action.payload.assignees || [])) {
        const { relabeled } = buildGenericSpeakerLabels(incomingSpeakers);

        state.speakers = relabeled;
        state.speakerNames = relabeled.map((speaker) => speaker.name);
      } else {
        const previousSpeakers = state.speakers || [];
        const mergedSpeakers = incomingSpeakers.map((incoming) => {
          const match = previousSpeakers.find(
            (prev) => prev.tag === incoming.tag || prev.name === incoming.name
          );
          if (!match) return incoming;
          return {
            ...incoming,
            type: incoming.type ?? match.type ?? "",
          };
        });

        state.speakers = mergedSpeakers;
        state.speakerNames = incomingSpeakers.map((speaker) => speaker.name);
      }
      state.lastSavedAt =
        action.payload.transcript.updated_at || action.payload.updated_at;
      state.aiQcLimit = action.payload.processed_data?.transcript_validation_meta?.count || 0;
      state.speakerValidationLimit = action.payload.processed_data?.speaker_validation_meta?.count || 0;
      state.speakerAttributionLimit = action.payload.processed_data?.speaker_attribution_meta?.count || 0;

      const savedFocusMode = localStorage.getItem("editorFocusMode");

      if (savedFocusMode !== null) {
        state.isFocusMode = JSON.parse(savedFocusMode);
      }

      const fileDetails = [];

      action.payload.workflowData?.details.forEach(({ key, label }) => {
        if (action.payload[key] !== undefined && action.payload[key] !== null) {
          fileDetails.push({ label, value: action.payload[key] });
        }
      });
      state.fileDetails = fileDetails;
    },

    initializeTranscripts: (state, action) => {
      const { transcripts } = action.payload;

      state.transcriptsInitialized = true;
      state.transcripts = transcripts.map((transcript) => {
        if (transcript?.type === "snippet") {
          return transcript;
        }
        const snippet = {
          type: "snippet",
          id: transcript.id,
          start_timestamp: transcript.start_timestamp,
          start: transcript.start,
          speaker: transcript.speaker,
          question_type: transcript.question_type ?? "",
          children: [
            {
              type: "paragraph",
              snippetId: transcript.id,
              children: transcript.words.map((word, index) => {
                return {
                  ...word,
                  id: index,
                  snippetId: transcript.id,
                };
              }),
            },
          ],
        };

        return snippet;
      });

      // Set baseline transcripts only if not already set (first load)
      if (
        !state.baselineTranscripts ||
        state.baselineTranscripts.length === 0
      ) {
        state.baselineTranscripts = JSON.parse(
          JSON.stringify(state.transcripts)
        );
      }

      const event = new CustomEvent("transcript:initialized");

      window.dispatchEvent(event);
    },

    resetBaselineTranscripts: (state, action) => {
      state.baselineTranscripts = action.payload;
    },

    initializeEntities: (state, action) => {
      const allEntities = { ...action.payload.processed_data.entities }; // Create a shallow copy
      delete allEntities["GPE"]; // Remove GPE key/value
      const entities = Object.keys(allEntities || {});
      const colorMap = {};

      entities.forEach((entity, index) => {
        colorMap[entity] = {
          color: ColorMap[index] || ColorMap[0],
          enabled: true,
        };
      });

      state.entities = allEntities;
      state.entitiesMap = invertEntities(allEntities);
      state.colorMap = colorMap;
    },

    initializeComments: (state, action) => {
      const { transcript } = action.payload;
      const comments = [];

      transcript.forEach((t) => {
        if (t.comments && t.comments.length) {
          t.comments.forEach((comment) => {
            comments.push({
              snippetId: t.id,
              commentId: comment.id,
              start: comment.start_pos,
              end: comment.end_pos,
              comment: comment.comment,
              timestamp: t.start_timestamp,
              name: comment.created_by.first_name,
            });
          });
        }
      });

      state.comments = comments;
    },

    updateTranscriptValidation: (state, action) => {
      if (!state.workflowData.processed_data.transcript_validation) {
        state.workflowData.processed_data.transcript_validation = {};
      }

      if (!state.workflowData.processed_data.transcript_validation_meta) {
        state.workflowData.processed_data.transcript_validation_meta = {};
      }

      if (!state.workflowData.processed_data.transcript_validation_feedback) {
        state.workflowData.processed_data.transcript_validation_feedback = [];
      }

      state.workflowData.processed_data.transcript_validation.validation_results = action.payload.validation;
      state.workflowData.processed_data.transcript_validation_meta = action.payload.meta;
      state.workflowData.processed_data.transcript_validation_feedback = action.payload.feedback;
      state.aiQcLimit = action.payload.meta.count || 0;
    },

    updateSpeakerAttribution: (state, action) => {
      if (!state.workflowData.processed_data.speaker_attribution) {
        state.workflowData.processed_data.speaker_attribution = {};
      }

      if (!state.workflowData.processed_data.speaker_attribution_meta) {
        state.workflowData.processed_data.speaker_attribution_meta = {};
      }

      state.workflowData.processed_data.speaker_attribution.validation_results = action.payload.validation;
      state.workflowData.processed_data.speaker_attribution_meta = action.payload.meta;
      state.speakerAttributionLimit = action.payload.meta.count || 0;
    },

    updateSummary: (state, action) => {
      state.summary = action.payload;
      state.hasUnsavedChanges = true;
    },

    setTranscripts: setTranscriptData,
    appendTranscripts: appendTranscriptData,
    strikeToggle: toggleStrike,
    splitParagraph: split,
    mergeParagraph: merge,
    updateSpeaker: changeSpeaker,
    cycleSpeaker: rotateSpeaker,

    toggleAllEntities: (state, action) => {
      const colorMap = JSON.parse(JSON.stringify(state.colorMap));

      for (let key in colorMap) {
        colorMap[key] = { ...colorMap[key], enabled: action.payload };
      }

      state.colorMap = colorMap;
    },

    toggleEntity: (state, action) => {
      const colorMap = JSON.parse(JSON.stringify(state.colorMap));

      colorMap[action.payload.entity] = {
        ...colorMap[action.payload.entity],
        enabled: action.payload.enabled,
      };
      state.colorMap = colorMap;
    },

    toggleAllAccuracy: (state, action) => {
      const setting = action.payload;

      state.accuracy = { bad: setting, good: setting, excellent: setting };
    },

    toggleAccuracy: (state, action) => {
      const accuracy = JSON.parse(JSON.stringify(state.accuracy));

      accuracy[action.payload.type] = action.payload.setting;

      state.accuracy = accuracy;
    },

    setIsSaving: (state, action) => {
      state.isSaving = action.payload;
    },

    updateHasUnsavedEntities: (state, action) => {
      state.hasUnsavedEntities = action.payload;
    },

    updateHasUnsavedInsights: (state, action) => {
      state.hasUnsavedInsights = action.payload;
    },

    updateHasUnsavedKeywords: (state, action) => {
      state.hasUnsavedKeywords = action.payload;
    },

    updateWorkflowStatus: (state, action) => {
      state.workflowData.workflow_status = action.payload;
    },

    updateReviewComplete: (state, action) => {
      state.workflowData.review_complete = action.payload;
    },

    markChangesSaved: (state) => {
      state.hasUnsavedChanges = false;
    },

    changesSaved: (state) => {
      state.hasUnsavedChanges = false;
      state.isSaving = false;
      state.lastSavedAt = Date.now();
    },

    updateLastSaved: (state) => {
      // Used from async thunks to update the last saved time
      state.lastSavedAt = Date.now();
    },

    workflowChanged: (state) => {
      state.hasUnsavedChanges = true;
    },

    setEditorMode: (state, action) => {
      state.editorMode = action.payload;
    },

    highlight: (state, action) => {
      const selection = window.getSelection();

      const anchorParent = selection.anchorNode.parentElement;
      const focusParent = selection.focusNode.parentElement;
      const text = selection
        .toString()
        .replace(/\n+/, " ")
        .trim()
        .replace(/[.,?]$/g, "");
      const startIndex = parseInt(anchorParent.dataset.index);
      const endIndex = parseInt(focusParent.dataset.index);
      const snippetId = action.payload;
      const entity = anchorParent.parentElement.dataset.entity || null;

      state.highlightedText = { text, startIndex, endIndex, snippetId, entity };

      if (selection.type.toLowerCase() === "range") {
        state.highlightAnchor = startIndex;
      }

      // check if the selection includes partial selection of the previous
      // node, and if so, disable entity selection.
      if (selection.toString().startsWith(" ")) {
        state.isEntityHighlightDisabled = true;
      } else {
        state.isEntityHighlightDisabled = false;
      }
    },

    setHighlightedText: (state, action) => {
      state.highlightedText = action.payload;
    },

    addEntity: (state, action) => {
      const entities = JSON.parse(JSON.stringify(state.entities)) || {};
      const term = action.payload;
      const cleaner = new RegExp(/[.?!]+$/);
      const thing = window.getSelection().toString().replace(cleaner, "");

      if (entities[term]) {
        // Does the currently highlighted thing(s) have an entity term?
        const existingTerm = state.entitiesMap[thing] || null;
        let mapToList;

        if (existingTerm) {
          // Remove previous term
          entities[term] = entities[term].filter((item) => item !== thing);
        }

        state.entitiesMap[thing] = term;
        mapToList = Object.entries(state.entitiesMap);

        // Group by terms
        state.entities = mapToList.reduce((acc, value) => {
          if (!acc[value[1]]) {
            acc[value[1]] = [];
          }

          acc[value[1]].push(value[0]);

          return acc;
        }, {});

        state.hasUnsavedChanges = true;
      }
    },

    updateInsights: (state, action) => {
      const insights = JSON.parse(JSON.stringify(state.insights));
      if ("summary" in action.payload) {
        insights[action.payload.index].summary = action.payload.summary || "";
      }
      if ("timestamp" in action.payload) {
        insights[action.payload.index].timestamp =
          action.payload.timestamp || "";
      }
      state.insights = insights;
      state.hasUnsavedInsights = true;
      state.hasUnsavedChanges = true;
    },

    updateKeywords: (state, action) => {
      state.keywords = action.payload;
      state.hasUnsavedKeywords = true;
      state.hasUnsavedChanges = true;
    },

    updateEntities: (state, action) => {
      state.entities = action.payload;
      state.hasUnsavedEntities = true;
      state.hasUnsavedChanges = true;
    },

    updateComments: (state, action) => {
      const { snippetId, data } = action.payload;
      const index = state.transcripts.findIndex((t) => t.id === snippetId);

      state.comments.push({
        snippetId,
        commentId: data.id,
        comment: data.comment,
        start: data.start_pos,
        end: data.end_pos,
        timestamp: state.transcripts[index].start_timestamp,
        name: data.created_by.first_name,
      });
    },

    removeAComment: (state, action) => {
      const index = state.comments.findIndex(
        (comment) => comment.commentId === action.payload
      );

      if (index > -1) {
        state.comments.splice(index, 1);
      }
    },

    updateAComment: (state, action) => {
      state.comments = state.comments.map((comment) => {
        if (comment.commentId === action.payload.id) {
          return { ...comment, comment: action.payload.text };
        } else {
          return comment;
        }
      });
    },

    updateSearchTerm: (state, action) => {
      const { searchTerm, matchCase } = action.payload;

      state.searchTerm = searchTerm;
      state.isCaseSensitive = matchCase || false;
    },

    updateReplaceTerm: (state, action) => {
      state.replaceTerm = action.payload;
    },

    setFindCaseSensitivity: (state, action) => {
      state.isCaseSensitive = action.payload;
    },

    toggleManualSave: (state, action) => {
      state.triggerManualSave = action.payload;
    },

    setReplaceLoading: (state, action) => {
      state.isReplaceLoading = action.payload;
    },

    findReplaceInSummary: replaceInSummary,
    findReplaceInChapters: replaceInChapters,
    simpleFinder: simpleFind,
    complexFinder: complexFind,

    updateSpeakersList: (state, action) => {
      const previousSpeakers = state.speakers || [];
      const nextSpeakers = action.payload || [];

      // Map tags to names for quick lookup
      const tagToNewName = new Map();
      nextSpeakers.forEach((s) => {
        if (s && s.tag) tagToNewName.set(s.tag, s.name);
      });

      // When speakers are updated (e.g., company removed), reflect changes in transcript snippet labels
      const event = new CustomEvent("editor.speakerTags.update", {
        detail: {
          speakers: previousSpeakers,
          tags: tagToNewName,
        },
      });

      dispatchEvent(event);

      state.speakers = nextSpeakers;
      state.speakerNames = nextSpeakers.map((speaker) => speaker.name);
    },

    setIsWorkflowSaving: (state, action) => {
      state.isWorkflowSaving = action.payload;
    },

    setIsWorkflowSaved: (state, action) => {
      state.isWorkflowSaved = action.payload;
    },

    toggleIsDownloading: (state) => {
      state.isDownloading = !state.isDownloading;
    },

    toggleIsDownloaded: (state) => {
      state.isDownloaded = !state.isDownloaded;
    },

    toggleEditorHints: (state) => {
      state.isHintsOpen = !state.isHintsOpen;
    },

    setVersion: (state, action) => {
      state.version = action.payload;
    },

    setVersionConflict: (state, action) => {
      state.versionConflict = action.payload;
    },

    setAcceptedMembers: (state, action) => {
      state.acceptedMembers = action.payload;
    },

    setChats: (state, action) => {
      state.chats = action.payload;
    },

    updateChats: (state, action) => {
      state.chats.push(action.payload);
    },

    toggleFocusMode: (state) => {
      state.isFocusMode = !state.isFocusMode;
      // Persist focus mode to localStorage
      localStorage.setItem(
        "editorFocusMode",
        JSON.stringify(state.isFocusMode)
      );
    },

    setTranscriptDiffMode: (state, action) => {
      state.isTranscriptDiffModeOn = action.payload;
    },

    setIsTranscriptDiffLoading: (state, action) => {
      state.isTranscriptDiffLoading = action.payload;
    },

    setTranscriptSources: (state, action) => {
      const { version, transcript } = action.payload;
      state.transcriptSources[version] = transcript;
    },

    setSourceVersion: (state, action) => {
      state.sourceVersion = action.payload;
    },

    setTargetVersion: (state, action) => {
      state.targetVersion = action.payload;
    },

    setVersionLabels: (state, action) => {
      const { type, label } = action.payload;

      state.versionLabels[type] = label;
    },

    setDiffVersionKeys: (state, action) => {
      const { source, target } = action.payload || {};

      state.diffVersionKeys.source = source || null;
      state.diffVersionKeys.target = target || null;
    },

    resetTranscriptDiff: (state) => {
      state.sourceVersion = null;
      state.targetVersion = null;
      state.versionLabels.source = null;
      state.versionLabels.target = null;
      state.diffVersionKeys.source = null;
      state.diffVersionKeys.target = null;
      state.isTranscriptDiffModeOn = false;
    },

    removeValidationResult: (state, action) => {
      const { category, entity, startTime } = action.payload;

      if (
        !state.workflowData?.processed_data?.transcript_validation
          ?.validation_results
      ) {
        return;
      }

      const validationResults =
        state.workflowData.processed_data.transcript_validation
          .validation_results;

      const updatedResults = validationResults.filter(
        (item) =>
          !(
            item.category === category &&
            item.entity === entity &&
            item.start_time === startTime
          )
      );

      state.workflowData.processed_data.transcript_validation.validation_results =
        updatedResults;
    },

    // Optimistically push/merge an editor-feedback row keyed by (category, entity, start_time).
    // Keeps validation_results intact (unlike removeValidationResult) so the drawer can show the
    // flag under Resolved, and so the resolved-set / complete gate stay in sync without a reload.
    upsertValidationFeedback: (state, action) => {
      const row = action.payload;
      if (!row || !state.workflowData?.processed_data) {
        return;
      }

      const pd = state.workflowData.processed_data;
      if (!Array.isArray(pd.transcript_validation_feedback)) {
        pd.transcript_validation_feedback = [];
      }

      const list = pd.transcript_validation_feedback;
      const idx = list.findIndex(
        (f) =>
          f.category === row.category &&
          f.entity === row.entity &&
          f.start_time === row.start_time
      );

      if (idx === -1) {
        list.push(row);
      } else {
        list[idx] = { ...list[idx], ...row };
      }
    },

    setAIQCLimit: (state, action) => {
      state.aiQcLimit = action.payload.limit || 0;
    },

    incrementAIQCLimit: (state) => {
      state.aiQcLimit = state.aiQcLimit + 1;
    },

    setSpeakerValidationLimit: (state, action) => {
      state.speakerValidationLimit = action.payload.limit || 0;
    },

    incrementSpeakerValidationLimit: (state) => {
      state.speakerValidationLimit = state.speakerValidationLimit + 1;
    },

    removeSpeakerAttributionResult: (state, action) => {
      const { category, entity, startTime } = action.payload;

      if (
        !state.workflowData?.processed_data?.speaker_attribution
          ?.validation_results
      ) {
        return;
      }

      const results =
        state.workflowData.processed_data.speaker_attribution.validation_results;

      state.workflowData.processed_data.speaker_attribution.validation_results =
        results.filter(
          (item) =>
            !(
              item.category === category &&
              item.entity === entity &&
              item.start_time === startTime
            )
        );
    },

    setSpeakerAttributionLimit: (state, action) => {
      state.speakerAttributionLimit = action.payload.limit || 0;
    },

    incrementSpeakerAttributionLimit: (state) => {
      state.speakerAttributionLimit = state.speakerAttributionLimit + 1;
    },

    setRedHerringIds: (state, action) => {
      state.redHerringIds = action.payload;
    },
  },
});

export const {
  reset,
  initializeWorkflow,
  initializeEntities,
  initializeComments,
  updateTranscriptValidation,
  appendComment,
  updateSummary,
  setTranscripts,
  appendTranscripts,
  initializeTranscripts,
  resetBaselineTranscripts,
  strikeToggle,
  updateSpeaker,
  cycleSpeaker,
  splitParagraph,
  mergeParagraph,
  toggleAllEntities,
  toggleEntity,
  toggleAllAccuracy,
  toggleAccuracy,
  setIsSaving,
  updateHasUnsavedEntities,
  updateHasUnsavedInsights,
  updateHasUnsavedKeywords,
  updateWorkflowStatus,
  updateReviewComplete,
  changesSaved,
  markChangesSaved,
  updateLastSaved,
  setEditorMode,
  workflowChanged,
  highlight,
  setHighlightedText,
  addEntity,
  updateInsights,
  updateEntities,
  updateKeywords,
  updateComments,
  removeAComment,
  updateAComment,
  updateSearchTerm,
  updateReplaceTerm,
  setFindCaseSensitivity,
  toggleManualSave,
  setReplaceLoading,
  findReplaceInSummary,
  findReplaceInChapters,
  simpleFinder,
  complexFinder,
  updateSpeakersList,
  setIsWorkflowSaving,
  setIsWorkflowSaved,
  toggleIsDownloading,
  toggleIsDownloaded,
  toggleEditorHints,
  setVersion,
  setVersionConflict,
  setAcceptedMembers,
  toggleFocusMode,
  setTranscriptDiffMode,
  setIsTranscriptDiffLoading,
  setTranscriptSources,
  setSourceVersion,
  setTargetVersion,
  setVersionLabels,
  setDiffVersionKeys,
  resetTranscriptDiff,
  setChats,
  updateChats,
  removeValidationResult,
  upsertValidationFeedback,
  setAIQCLimit,
  incrementAIQCLimit,
  setSpeakerValidationLimit,
  incrementSpeakerValidationLimit,
  updateSpeakerAttribution,
  removeSpeakerAttributionResult,
  setSpeakerAttributionLimit,
  incrementSpeakerAttributionLimit,
  setRedHerringIds,
} = editorSlice.actions;

// Selectors
export const getWorkflowData = (state) => state.editor.workflowData;
export const getCurrentStatus = (state) =>
  state.editor.workflowData?.workflow_status;
export const getUploadType = (state) => state.editor.workflowData?.upload_type;
export const getQuestionClassificationEnabled = (state) =>
  Boolean(state.editor.workflowData?.question_classification);
export const selectWorkflowId = (state) => state.editor.workflowData?.id;
export const selectHeadline = (state) =>
  state.editor.workflowData?.processed_data.headline;
export const getSummary = (state) => state.editor.summary;
export const getContextSummary = (state) => state.editor.contextSummary;
export const getKeywords = (state) => state.editor.keywords;
export const getAudioUri = (state) => state.editor.workflowData?.public_url;
export const getOriginalAudioUri = (state) =>
  state.editor.workflowData?.public_url_original;
export const getDuration = (state) =>
  state.editor.workflowData?.processed_data.duration;
export const getStoredDuration = (state) =>
  state.editor.workflowData?.duration_seconds ??
  state.editor.workflowData?.processed_data?.duration ??
  0;
export const getTitle = (state) => state.editor.workflowData?.title;
export const getEditingStart = (state) =>
  state.editor.workflowData?.editing_start_time;
export const getAssignees = (state) => state.editor.workflowData?.assignees;
export const selectTranscripts = (state) => state.editor.transcripts;
export const selectTranscriptsInitialized = (state) =>
  state.editor.transcriptsInitialized;
export const selectTranscriptUpdates = (state) =>
  state.editor.transcriptUpdates;
export const getSpeakers = (state) => state.editor.speakers;
export const getSpeakerNames = (state) => state.editor.speakerNames;
export const selectEntities = (state) => state.editor.entities;
export const selectEntitiesMap = (state) => state.editor.entitiesMap;
export const selectInsights = (state) => state.editor.insights;
export const selectKeywords = (state) => state.editor.keywords;
export const selectComments = (state) => state.editor.comments;
export const selectAccuracy = (state) => state.editor.accuracy;
export const selectGlossary = (state) => {
  const raw = state.editor.workflowData?.processed_data?.qa_research;
  return Array.isArray(raw) ? raw : [];
};
export const selectEditorMode = (state) => state.editor.editorMode;
export const selectHasUnsavedChanges = (state) =>
  state.editor.hasUnsavedChanges;
export const getLastSavedAt = (state) => state.editor.lastSavedAt;
export const selectColorMap = (state) => state.editor.colorMap;
export const getHighlightedText = (state) => state.editor.highlightedText;
export const getHighlightAnchor = (state) => state.editor.highlightAnchor;
export const getIsEntityHighlightDisabled = (state) =>
  state.editor.isEntityHighlightDisabled;
export const selectSearchTerm = (state) => state.editor.searchTerm;
export const selectReplaceTerm = (state) => state.editor.replaceTerm;
export const getMatchCase = (state) => state.editor.isCaseSensitive;
export const shouldSaveManually = (state) => state.editor.triggerManualSave;
export const getIsReplaceLoading = (state) => state.editor.isReplaceLoading;
export const getIsSaving = (state) => state.editor.isSaving;
export const getIsWorkflowSaving = (state) => state.editor.isWorkflowSaving;
export const getIsWorkflowSaved = (state) => state.editor.isWorkflowSaved;
export const getIsDownloading = (state) => state.editor.isDownloading;
export const getIsDownloaded = (state) => state.editor.isDownloaded;
export const getIsHintsOpen = (state) => state.editor.isHintsOpen;
export const selectVersion = (state) => state.editor.version;
export const selectVersionConflict = (state) => state.editor.versionConflict;
export const getAcceptedMembersFromState = (state) =>
  state.editor.acceptedMembers;
export const getPartnerUsers = (state) =>
  state.editor.acceptedMembers.filter((member) => member.role === "PARTNER");
export const getIsFocusMode = (state) => state.editor.isFocusMode;
export const selectBaselineTranscripts = (state) =>
  state.editor.baselineTranscripts;
export const selectRedHerringIds = (state) => state.editor.redHerringIds;
export const selectIsTranscriptDiffModeOn = (state) =>
  state.editor.isTranscriptDiffModeOn;
export const selectIsTranscriptDiffLoading = (state) =>
  state.editor.isTranscriptDiffLoading;
export const selectTranscriptSources = (state) =>
  state.editor.transcriptSources;
export const selectSourceVersion = (state) => state.editor.sourceVersion;
export const selectTargetVersion = (state) => state.editor.targetVersion;
export const selectVersionLabels = (state) => state.editor.versionLabels;
export const selectDiffVersionKeys = (state) => state.editor.diffVersionKeys;
export const getChats = (state) => state.editor.chats;
export const selectValidationResults = (state) =>
  state.editor.workflowData?.processed_data?.transcript_validation
    ?.validation_results;
export const selectValidationFeedback = (state) =>
  state.editor.workflowData?.processed_data?.transcript_validation_feedback;
export const selectWordCount = (state) => state.editor.wordCount;
export const selectAIQCLimit = (state) => state.editor.aiQcLimit;
export const selectSpeakerValidationLimit = (state) => state.editor.speakerValidationLimit;
export const selectSpeakerAttributionResults = (state) =>
  state.editor.workflowData?.processed_data?.speaker_attribution
    ?.validation_results;
export const selectSpeakerAttributionLimit = (state) => state.editor.speakerAttributionLimit;
export const selectCallType = (state) => state.editor.workflowData?.call_type;
export const selectIsDeltaUpdatesEnabled = (state) =>
  isDeltaUpdatesEnabledForWorkflow(state.editor.workflowData);
// Batch automation-review flow: strict `=== true` so workflows missing the field behave as before.
export const selectIsAutomationReview = (state) =>
  state.editor.workflowData?.automation_review === true;

// Async Thunks
export const fetchNewTranscripts = createAsyncThunk(
  "editor/fetchNewTranscripts",
  async (workflowId, thunkAPI) => {
    const { getState, dispatch } = thunkAPI;

    if (!canFetchNewTranscripts(getState().editor)) {
      return { newCount: 0 };
    }

    const alreadyHave = serverTranscriptCount(getState().editor.transcripts);

    try {
      const transcript = await getPaginatedWorkflowTranscript(
        workflowId,
        alreadyHave,
        TRANSCRIPT_PAGE_SIZE
      );
      const newItems = transcript?.transcript || [];

      if (newItems.length === 0) {
        return { newCount: 0 };
      }

      // Re-read after the await: getWorkflow (or another poll) may have landed
      // while this request was in flight. Concat onto that live list, never the
      // snapshot from thunk start — that snapshot is often [] and would wipe
      // the first-load tree, then emit a full-page append onto Slate.
      // Drop the result if the user has navigated to a different file.
      if (!shouldApplyInFlightTranscript(getState().editor, workflowId)) {
        return { newCount: 0 };
      }

      const { nextStore, storeLengthBeforeAppend } = appendLiveStorePage(
        getState().editor.transcripts,
        newItems
      );

      if (nextStore.length === storeLengthBeforeAppend) {
        return { newCount: 0 };
      }

      dispatch(initializeTranscripts({ transcripts: nextStore }));

      const appended = storeAppendedTail(
        getState().editor.transcripts,
        storeLengthBeforeAppend
      );
      emitTranscriptAppend(appended, workflowId);

      return { newCount: nextStore.length - storeLengthBeforeAppend };
    } catch (err) {
      console.error(err);
      return { newCount: 0 };
    }
  }
);

export const initTranscriptDiff = createAsyncThunk(
  "editor/initTranscriptDiff",
  async (payload, thunkAPI) => {
    const { dispatch, getState, rejectWithValue } = thunkAPI;
    const { editor } = getState();
    const { workflowId, sourceVersion, targetVersion } = payload;

    dispatch(setDiffVersionKeys({ source: sourceVersion, target: targetVersion }));
    dispatch(setIsTranscriptDiffLoading(true));

    // The transcript endpoint serves the live transcript when a stage backup is missing, so a stage
    // inferred from workflow_status but never written would blame its actor for every later edit.
    let availableKeys = availableDiffVersionKeys(editor.workflowData);
    let backupListLoaded = false;

    try {
      const { versions } = await getTranscriptBackupVersions(workflowId);

      availableKeys = versionKeysFromBackups(versions);
      backupListLoaded = true;
    } catch (err) {
      // Metadata call is best-effort; fall back to the stage inference.
    }

    if (backupListLoaded) {
      const missing = missingRequiredVersionKeys(
        sourceVersion,
        targetVersion,
        availableKeys
      );

      if (missing.length > 0) {
        const message = missingSnapshotMessage(missing[0]);

        missing.forEach((key) => {
          dispatch(setTranscriptSources({ version: key, transcript: null }));
        });
        dispatch(setIsTranscriptDiffLoading(false));
        dispatch(showToast({ message, severity: "error" }));

        return rejectWithValue(new Error(message));
      }
    }

    const intermediateKeys = getIntermediateVersionKeys(
      sourceVersion,
      targetVersion,
      availableKeys
    );

    // Drop snapshots cached by an earlier compare that the server does not actually hold.
    getDiffHopKeys(sourceVersion, targetVersion)
      .filter((key) => key !== "current" && !availableKeys.includes(key))
      .forEach((key) => dispatch(setTranscriptSources({ version: key, transcript: null })));

    const ensureVersion = async (versionKey, { required }) => {
      if (!versionKey || versionKey === "current") {
        return null;
      }

      const cached = getState().editor.transcriptSources[versionKey];

      if (cached?.length > 0) {
        return cached;
      }

      try {
        const data = await getTranscriptVersion(workflowId, versionKey);
        const transcript = deserialize(data.transcript);

        dispatch(setTranscriptSources({ version: versionKey, transcript }));

        return transcript;
      } catch (err) {
        if (required) {
          throw err;
        }

        return null;
      }
    };

    try {
      await Promise.all([
        ensureVersion(sourceVersion, { required: true }),
        ensureVersion(targetVersion, { required: targetVersion !== "current" }),
        ...intermediateKeys.map((key) => ensureVersion(key, { required: false })),
      ]);
    } catch (err) {
      dispatch(setIsTranscriptDiffLoading(false));
      dispatch(
        showToast({
          message: "Transcript version comparison could not be initialized",
          severity: "error",
        }),
      );

      return rejectWithValue(err);
    }

    const sources = getState().editor.transcriptSources;

    dispatch(setSourceVersion(sources[sourceVersion]));

    if (targetVersion === "current") {
      // Always re-read the live editor: a cached `current` is a snapshot of the last compare,
      // so reusing it would diff against stale text and misdate the attribution.
      const event = new CustomEvent("transcript.diffviewer.setCurrentVersion");

      window.dispatchEvent(event);

      return;
    }

    dispatch(setTargetVersion(sources[targetVersion]));
    dispatch(setIsTranscriptDiffLoading(false));
    dispatch(setTranscriptDiffMode(true));
  }
);

export const getWorkflow = createAsyncThunk(
  "editor/getWorkflow",
  async (id, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;

    try {
      const data = await getWorkflowTranscript(id);

      dispatch(initializeWorkflow(data));
      try {
        const partnerConfig = getPartnerSpeakerConfig(data?.assignees || []);
        const allowedSpeakerTypes = partnerConfig.types.map((t) => t.type);
        dispatch(
          initializeFromProcessedSpeakers({
            speakers: data?.processed_data?.speakers || [],
            allowedSpeakerTypes,
            strictInference: partnerConfig.strictInference,
            genericNumbered: partnerConfig.genericNumbered === true,
          })
        );
      } catch (e) {
        // noop
      }
      dispatch(setVersion(data?.processed_data?.version || 1));
      dispatch(initializeEntities(data));

      const prefix = data.transcript?.transcript || [];
      const remaining = await fetchRemainingTranscriptPages(
        id,
        nextTranscriptPageStart(prefix),
        getPaginatedWorkflowTranscript
      );

      if (String(getState().editor.workflowData?.id) !== String(id)) {
        return;
      }

      if (remaining.hitPageCap) {
        dispatch(
          showToast({
            message: TRANSCRIPT_PAGE_CAP_MESSAGE,
            severity: "warning",
          })
        );
      }

      const rawTranscripts = concatTranscriptPages(prefix, remaining.items);

      let withRedHerrings;

      // Automation-review flow validates AI flags only, so never inject red herrings.
      if (data.automation_review === true) {
        withRedHerrings = rawTranscripts;
      } else {
        const savedState = loadRedHerringState(data.id, data.workflow_status);

        if (savedState === null) {
          withRedHerrings = injectRandomSnippets(rawTranscripts, {
            uploadType: data.upload_type,
            workflowStatus: data.workflow_status,
          });
          const snapshot = snapshotRedHerringState(withRedHerrings);

          saveRedHerringState(data.id, data.workflow_status, snapshot);
        } else if (savedState.length === 0) {
          withRedHerrings = rawTranscripts;
        } else {
          withRedHerrings = restoreRedHerringState(rawTranscripts, savedState, {
            uploadType: data.upload_type,
            workflowStatus: data.workflow_status,
          });
        }
      }

      const injectedIds = withRedHerrings
        .filter((t) => t.isRedHerring)
        .map((t) => t.id);

      // Generic partners (e.g. Third Bridge): relabel paragraph speakers to their
      // positional labels before they enter the store, so existing files show
      // "Speaker N" on open without flagging unsaved changes.
      if (usesGenericSpeakers(data?.assignees || [])) {
        withRedHerrings = remapGenericParagraphSpeakers(
          withRedHerrings,
          data?.processed_data?.speakers || []
        );
      }

      dispatch(setRedHerringIds(injectedIds));
      dispatch(
        initializeTranscripts({ transcripts: withRedHerrings })
      );

      // Comments
      setTimeout(() => {
        if (String(getState().editor.workflowData?.id) !== String(id)) return;
        dispatch(
          initializeComments({
            transcript: rawTranscripts,
          })
        );
      }, 1000);
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;

      dispatch(showToast({ message: errorMessage, severity: "error" }));
    }
  }
);

export const updateData = createAsyncThunk(
  "editor/updateData",
  async (payload, thunkAPI) => {
    const { dispatch, getState, rejectWithValue } = thunkAPI;
    const { editor } = getState();
    const isDeltaUpdates = isDeltaUpdatesEnabledForWorkflow(editor.workflowData);
    const skip = (reason) => rejectWithValue({ skipped: true, reason });

    if (editor.hasUnsavedChanges === false) {
      return skip("clean");
    }

    if (editor.isSaving) {
      // Don't treat this as a successful save — AutoSaver would otherwise
      // advance its confirmed baseline and drop speaker-tag (and other) edits.
      return skip("in_flight");
    }

    if (editor.transcripts && editor.transcripts.length === 0) {
      return skip("empty_store");
    }

    if (isDeltaUpdates && (!payload?.transcriptUpdates || payload?.transcriptUpdates.length === 0)) {
      return skip("empty_delta");
    }

    if (!isDeltaUpdates && (!payload?.fullTranscript || payload?.fullTranscript.length === 0)) {
      return skip("empty_full");
    }

    const safeFullTranscript = stripRedHerringWords(payload?.fullTranscript || []);

    const request = {
      // abstract: editor.summary,
      // headline: editor.workflowData.processed_data.headline,
      // entities: editor.entities,
      version: editor.version,
      tr: isDeltaUpdates
        ? (payload?.transcriptUpdates || [])
        : serialize(safeFullTranscript),
      ...(editor.hasUnsavedEntities && { entities: editor.entities }),
      ...(editor.hasUnsavedInsights && { insights: editor.insights }),
      ...(editor.hasUnsavedKeywords && { keywords: editor.keywords }),
    };
    const { id } = editor.workflowData;

    dispatch(setIsSaving(true));

    try {
      await saveWorkflow(id, request, { useDeltaEndpoint: isDeltaUpdates });
      dispatch(setVersion(editor.version + 1));
      dispatch(changesSaved());
      if (payload && payload.shouldReload) {
        window.location.reload();
      }
      dispatch(updateHasUnsavedEntities(false));
      dispatch(updateHasUnsavedInsights(false));
      dispatch(updateHasUnsavedKeywords(false));
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      const status = e.response?.status;
      if (status === 409) {
        try {
          const { fresh, mergedList, version, hitPageCap } =
            await reloadTranscriptAfterConflict(id, {
              getWorkflowTranscript,
              getPaginatedWorkflowTranscript,
            });

          if (!shouldApplyInFlightTranscript(getState().editor, id)) {
            return rejectWithValue({ conflict: true, stale: true });
          }

          if (hitPageCap) {
            dispatch(
              showToast({
                message: TRANSCRIPT_PAGE_CAP_MESSAGE,
                severity: "warning",
              })
            );
          }

          dispatch(setVersionConflict(true));
          dispatch(initializeWorkflow(fresh));
          dispatch(initializeTranscripts({ transcripts: mergedList }));
          dispatch(setVersion(version));

          const converted = getState().editor.transcripts;
          dispatch(resetBaselineTranscripts(JSON.parse(JSON.stringify(converted))));
          dispatch(markChangesSaved());
          emitTranscriptForceReplace(converted, id);

          return rejectWithValue({ conflict: true });
        } catch (fetchErr) {
          const fm = fetchErr.response?.data?.message || fetchErr.message;
          dispatch(
            showToast({ message: "Reload failed: " + fm, severity: "error" })
          );

          return rejectWithValue({ conflict: true, reloadFailed: true });
        }
      }

      dispatch(showToast({ message: errorMessage, severity: "error" }));

      return rejectWithValue({ error: true, message: errorMessage });
    } finally {
      dispatch(setIsSaving(false));
    }
  }
);

export const findAndReplace = createAsyncThunk(
  "editor/findReplace",
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { searchTerm, replaceTerm, isCaseSensitive, replaceAll, editor } = payload;

    let transcripts = JSON.parse(JSON.stringify(editor));
    const allModifiedIndices = new Set();
    const args = { find: searchTerm, replaceTerm, isCaseSensitive, replaceAll };

    // 1. Handle matches that live within a single text node
    const simpleResult = applySimpleReplace({ ...args, transcripts });

    transcripts = simpleResult.transcripts;
    simpleResult.modifiedIndices.forEach((i) => allModifiedIndices.add(i));

    // 2. Handle matches that span multiple text nodes
    const shouldRunComplex = replaceAll
      ? !simpleResult.didBreak
      : simpleResult.modifiedIndices.length === 0;

    if (shouldRunComplex) {
      const complexResult = applyComplexReplace({ ...args, transcripts });

      transcripts = complexResult.transcripts;
      complexResult.modifiedIndices.forEach((i) => allModifiedIndices.add(i));
    }

    // 3. Push updated paragraphs into Slate via DOM events
    allModifiedIndices.forEach((index) => {
      window.dispatchEvent(
        new CustomEvent("findAndReplace", {
          detail: { index, transcript: transcripts[index] },
        })
      );
    });

    if (allModifiedIndices.size > 0) {
      dispatch(workflowChanged());
    }
  }
);

export const saveSpeakers = createAsyncThunk(
  "editor/saveSpeakers",
  async (speakersList, thunkAPI) => {
    const { dispatch, getState, rejectWithValue } = thunkAPI;
    const { editor } = getState();
    const { id } = editor.workflowData;

    if (editor.isSaving) {
      return;
    }

    dispatch(setIsSaving(true));

    try {
      const response = await updateSpeakers(id, { speakers: speakersList });

      await dispatch(updateSpeakersList(speakersList));
      await dispatch(workflowChanged());
      // dispatch(updateData());

      try {
        dispatch(applySavedFormattedSpeakers(speakersList));
      } catch (e) {
        // noop
      }
      dispatch(updateLastSaved());
      dispatch(
        showToast({
          message: "Speakers have been updated",
          severity: "success",
          autoClose: true,
        })
      );

      return response;
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Speakers could not be saved. Please try again later. " +
            errorMessage,
          severity: "error",
        })
      );

      return rejectWithValue(e);
    } finally {
      dispatch(setIsSaving(false));
      // dispatch(getWorkflow(id));
    }
  }
);

export const saveComment = createAsyncThunk(
  "editor/saveComment",
  async (payload, { dispatch, getState }) => {
    const { editor } = getState();
    const { id } = editor.workflowData;
    const { comment, snippetId, startIndex, endIndex, highlightedText } =
      payload;
    const request = {
      highlighted_text: highlightedText,
      start_pos: startIndex,
      end_pos: endIndex,
      comment,
    };

    try {
      const data = await addComment(id, snippetId, request);

      dispatch(updateComments({ snippetId, data }));
      dispatch(updateLastSaved());
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Comment could not be saved. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    }
  }
);

export const fetchChats = createAsyncThunk(
  "editor/fetchChats",
  async (_, { dispatch, getState }) => {
    const { editor } = getState();
    const { id } = editor.workflowData;

    try {
      const { messages } = await getChats(id);
      dispatch(setChats(messages));
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Comment could not be saved. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    }
  }
);

export const saveChat = createAsyncThunk(
  "editor/saveChat",
  async (payload, { dispatch, getState }) => {
    const { editor } = getState();
    const { id } = editor.workflowData;

    try {
      const { data } = await addChats(id, { message: payload.message });
      dispatch(updateChats({ ...payload, message_id: data.message_id }));
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Comment could not be saved. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    }
  }
);

export const saveUpdatedComment = createAsyncThunk(
  "editor/updateComment",
  async (payload, { dispatch, getState }) => {
    const { editor } = getState();
    const { id } = editor.workflowData;
    const { snippetId, commentId, comment, callBack } = payload;
    const body = {
      _id: commentId,
      comment,
    };

    try {
      await updateComment({ docid: id, itemId: snippetId, commentId, body });
      dispatch(updateAComment({ id: commentId, text: comment }));
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Comment could not be updated. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    }
    callBack();
  }
);

export const removeComment = createAsyncThunk(
  "editor/removeComment",
  async (payload, { dispatch, getState }) => {
    const { editor } = getState();
    const { id } = editor.workflowData;
    const { snippetId, commentId, callBack } = payload;

    try {
      await deleteComment(id, snippetId, commentId);
      dispatch(removeAComment(commentId));
      dispatch(updateLastSaved());
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Comment could not be deleted. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    }
    callBack();
  }
);

export const updateWorkflow = createAsyncThunk(
  "editor/updateWorkflow",
  async (payload = {}, thunkAPI) => {
    const { editor } = thunkAPI.getState();
    const { transcripts } = editor;
    const { id, workflow_status } = editor.workflowData;
    const { dispatch } = thunkAPI;
    const nextStatus = getNextStatusFromWorkflow(editor.workflowData, workflow_status);

    // Mirror backend _validate_first_paragraph: first non-empty paragraph needs a speaker.
    // Old check (=== "" on transcripts[0]) let an undefined/null speaker or empty lead through.
    const snippetText = (s) =>
      (s?.children || [])
        .flatMap((p) => p?.children || [])
        .map((w) => w?.text || "")
        .join("")
        .trim();
    const firstContentSnippet = transcripts
      .slice(0, 2)
      .find((s) => snippetText(s) !== "");
    if (firstContentSnippet && !(firstContentSnippet.speaker || "").trim()) {
      dispatch(
        showToast({
          message: "The first paragraph of the transcript does not have a speaker tag. Add a speaker before submitting.",
          severity: "error",
        }),
      );

      return;
    }

    dispatch(setIsWorkflowSaving(true));

    try {
      await completeWorkflow(id);

      dispatch(updateWorkflowStatus(nextStatus));
      dispatch(updateLastSaved());
      dispatch(
        showToast({
          message: "File has been marked as completed.",
          severity: "success",
          autoClose: true,
        })
      );

      dispatch(setIsWorkflowSaved(true));
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message: "File could not be saved. Please try again. " + errorMessage,
          severity: "error",
        })
      );
    } finally {
      // dispatch(getWorkflow(id));
      setTimeout(() => {
        dispatch(setIsWorkflowSaving(false));
        dispatch(setIsWorkflowSaved(false));
      }, 2500);
    }
  }
);

export const downloadTranscript = createAsyncThunk(
  "editor/download",
  async (config, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const { editor } = getState();
    const { id } = editor.workflowData;

    dispatch(toggleIsDownloading());
    // await dispatch(updateData());

    try {
      const promises = config.formats.map(async (format) => {
        const res = await exportTranscript(id, format, config.options);
        const fileName = `${editor.workflowData.title}`.replace(".mp3", "");

        download(res, fileName, format);
      });

      // Give the DB enough time to save all the changes
      setTimeout(async () => {
        await Promise.all(promises);

        dispatch(toggleIsDownloaded());

        setTimeout(() => {
          dispatch(toggleIsDownloaded());
        }, 1000);

        config.callback && config.callback();
      }, 2000);
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;

      dispatch(
        showToast({
          message:
            "Transcript could not be exported at this time. Please try again. " +
            errorMessage,
          severity: "error",
        })
      );
    } finally {
      dispatch(toggleIsDownloading());
    }
  }
);

export const fetchAcceptedMembers = createAsyncThunk(
  "editor/fetchAcceptedMembers",
  async (_, thunkAPI) => {
    const { dispatch } = thunkAPI;

    try {
      const data = await getAcceptedMembers();
      dispatch(setAcceptedMembers(data.members || []));
      return data.members || [];
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message;
      dispatch(
        showToast({
          message:
            "Could not load team members. Please try again. " + errorMessage,
          severity: "error",
        })
      );
      return [];
    }
  }
);

// AI Validation Thunks
export const validateTranscript = createAsyncThunk(
  "editor/validateTranscript",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const workflowId = state.editor.workflowData?.id;
    const attempts = state.editor.aiQcLimit;

    if (!workflowId) {
      return rejectWithValue("No workflow ID available");
    }

    try {
      let inProgress = false;
      let timer = 60 * 1000;
      let startedAt = null;

      try {
        const hideTranscript = true; // we don't need the transcript data for validation
        const workflow = await getWorkflowTranscript(workflowId, hideTranscript);

        inProgress = (workflow.processed_data?.transcript_validation_meta?.status === "in_progress");
        startedAt = workflow.processed_data?.transcript_validation_meta?.started_at;
        timer = 30 * 1000;
      } catch (error) {
        // noop
      }

      if (!inProgress || (inProgress && (startedAt && (Date.now() / 1000 - startedAt) > AI_VALIDATION_RECHECK_TIME))) {
        if (attempts < MAX_AI_QC_ATTEMPTS) {
          const res = await validateTranscriptEntities(workflowId);
          // Accumulate mode: un-reviewed flags remain, so the server returned them instead of
          // starting a run. They are already shown in the panel; stop the spinner and nudge the
          // reviewer, and do not poll or consume an attempt.
          if (res?.status === "pending_review") {
            payload.callback && payload.callback();
            dispatch(
              showToast({
                message: `${res.pending_count ?? "Some"} flag(s) still need review before re-validating.`,
                severity: "info",
                autoClose: true,
              })
            );
            return { success: true };
          }
        } else {
          payload.callback && payload.callback();

          dispatch(
            showToast({
              message: "QA Quality Check maximum number of attempts reached, skipping...",
              severity: "warning",
            }),
          );

          return { success: true };
        }
      }

      // Schedule polling after 60 seconds
      setTimeout(() => {
        dispatch(pollWorkflowValidation(payload));
      }, timer);

      return { success: true };
    } catch (error) {
      const status = error.code;
      const errorMessage = error.response?.data?.message || error.message;

      if (status !== 409) {
        dispatch(
          showToast({
            message: "AI validation failed. Please try again. " + errorMessage,
            severity: "error",
          })
        );

        payload.callback && payload.callback();

        return rejectWithValue(errorMessage);
      } else {
        // Request is already in progress, handle silently and recheck progress
        setTimeout(() => {
          dispatch(pollWorkflowValidation(payload));
        }, 10 * 1000);
      }
    }
  }
);

export const pollWorkflowValidation = createAsyncThunk(
  "editor/pollWorkflowValidation",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const workflowId = state.editor.workflowData?.id;
    const attempts = state.editor.aiQcLimit;
    const { callback } = payload;

    if (!workflowId) {
      return rejectWithValue("No workflow ID available");
    }

    try {
      const hideTranscript = true; // we don't need the transcript data for validation
      const data = await getWorkflowTranscript(workflowId, hideTranscript);
      const validationStatus = data.processed_data?.transcript_validation_meta?.status;
      const validationResults = data.processed_data?.transcript_validation?.validation_results;

      // Check if process failed, and if so, trigger verification and start over
      if (validationStatus === "failed") {
        if (attempts < MAX_AI_QC_ATTEMPTS) {
          dispatch(validateTranscript(payload));

          return { hasResults: false, results: [] };
        }

        callback();

        dispatch(
          showToast({
            message: "QA Quality Check maximum number of attempts reached, skipping...",
            severity: "warning",
          })
        );

        return { hasResults: true, results: [] };
      }

      // Check if status is stuck in in_progress for over 3 minutes and if so, re-trigger verification
      if (validationStatus === "in_progress") {
        const startedAt = data.processed_data?.transcript_validation_meta?.started_at;
        const now = Date.now() / 1000;

        if (startedAt && (now - startedAt) > AI_VALIDATION_RECHECK_TIME) {
          if (attempts < MAX_AI_QC_ATTEMPTS) {
            dispatch(validateTranscript(payload));

            return { hasResults: false, results: [] };
          }

          callback();

          dispatch(
            showToast({
              message: "QA Quality Check maximum number of attempts reached, skipping...",
              severity: "warning",
            })
          );

          return { hasResults: true, results: [] };
        }

        // Still running and not stuck: keep polling so the panel auto-refreshes when the run
        // finishes, instead of falling through to a premature empty/stale result (which left
        // the drawer stale until a full page reload).
        setTimeout(() => {
          dispatch(pollWorkflowValidation(payload));
        }, 30 * 1000);

        return { hasResults: false, results: [] };
      }

      if (validationResults && validationResults.length > 0) {
        // Results are available, update workflow data directly
        dispatch(incrementAIQCLimit()); // increment only on success
        dispatch(
          updateTranscriptValidation({
            validation: validationResults,
            meta: data.processed_data?.transcript_validation_meta,
            feedback: data.processed_data?.transcript_validation_feedback || [],
          },
          ));
        callback();

        return {
          hasResults: true,
          results: validationResults,
        };
      }

      if (validationResults && validationResults.length === 0) {
        dispatch(incrementAIQCLimit()); // increment only on success
        dispatch(
          updateTranscriptValidation({
            validation: [],
            meta: data.processed_data?.transcript_validation_meta,
            feedback: data.processed_data?.transcript_validation_feedback || [],
          }),
        );
        callback();
        dispatch(
          showToast({
            message: "All QA Quality Checks resolved!",
            severity: "success",
            autoClose: true,
          }),
        );

        return {
          hasResults: true,
          results: [],
        };
      }

      // No results yet, schedule next poll in 30 seconds
      setTimeout(() => {
        dispatch(pollWorkflowValidation(payload));
      }, 30 * 1000);

      return { hasResults: false, results: [] };
    } catch (error) {
      console.log("Polling failed, trying again...", error);

      setTimeout(() => {
        dispatch(pollWorkflowValidation(payload));
      }, 10 * 1000);
    }
  }
);

// Speaker Attribution Thunks
export const validateSpeakerAttribution = createAsyncThunk(
  "editor/validateSpeakerAttribution",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const workflowId = state.editor.workflowData?.id;
    const attempts = state.editor.speakerAttributionLimit;

    if (!workflowId) {
      return rejectWithValue("No workflow ID available");
    }

    try {
      let inProgress = false;
      let timer = 60 * 1000;
      let startedAt = null;

      try {
        const hideTranscript = true;
        const workflow = await getWorkflowTranscript(workflowId, hideTranscript);

        inProgress = (workflow.processed_data?.speaker_attribution_meta?.status === "in_progress");
        startedAt = workflow.processed_data?.speaker_attribution_meta?.started_at;
        timer = 30 * 1000;
      } catch (error) {
        // noop
      }

      if (!inProgress || (inProgress && (startedAt && (Date.now() / 1000 - startedAt) > AI_VALIDATION_RECHECK_TIME))) {
        if (attempts < MAX_SPEAKER_ATTRIBUTION_ATTEMPTS) {
          await triggerSpeakerAttributionCheck(workflowId);
        } else {
          payload.callback && payload.callback();

          dispatch(
            showToast({
              message: "Speaker Attribution Check maximum number of attempts reached, skipping...",
              severity: "warning",
            }),
          );

          return { success: true };
        }
      }

      setTimeout(() => {
        dispatch(pollSpeakerAttributionValidation(payload));
      }, timer);

      return { success: true };
    } catch (error) {
      const status = error.code;
      const errorMessage = error.response?.data?.message || error.message;

      if (status !== 409) {
        dispatch(
          showToast({
            message: "Speaker Attribution Check failed. Please try again. " + errorMessage,
            severity: "error",
          })
        );

        payload.callback && payload.callback();

        return rejectWithValue(errorMessage);
      } else {
        setTimeout(() => {
          dispatch(pollSpeakerAttributionValidation(payload));
        }, 10 * 1000);
      }
    }
  }
);

export const pollSpeakerAttributionValidation = createAsyncThunk(
  "editor/pollSpeakerAttributionValidation",
  async (payload, { dispatch, getState, rejectWithValue }) => {
    const state = getState();
    const workflowId = state.editor.workflowData?.id;
    const attempts = state.editor.speakerAttributionLimit;
    const { callback } = payload;

    if (!workflowId) {
      return rejectWithValue("No workflow ID available");
    }

    try {
      const hideTranscript = true;
      const data = await getWorkflowTranscript(workflowId, hideTranscript);
      const attributionStatus = data.processed_data?.speaker_attribution_meta?.status;
      const attributionResults = data.processed_data?.speaker_attribution?.validation_results;

      if (attributionStatus === "failed") {
        if (attempts < MAX_SPEAKER_ATTRIBUTION_ATTEMPTS) {
          dispatch(validateSpeakerAttribution(payload));

          return { hasResults: false, results: [] };
        }

        callback();

        dispatch(
          showToast({
            message: "Speaker Attribution Check maximum number of attempts reached, skipping...",
            severity: "warning",
          })
        );

        return { hasResults: true, results: [] };
      }

      if (attributionStatus === "in_progress") {
        const startedAt = data.processed_data?.speaker_attribution_meta?.started_at;
        const now = Date.now() / 1000;

        if (startedAt && (now - startedAt) > AI_VALIDATION_RECHECK_TIME) {
          if (attempts < MAX_SPEAKER_ATTRIBUTION_ATTEMPTS) {
            dispatch(validateSpeakerAttribution(payload));

            return { hasResults: false, results: [] };
          }

          callback();

          dispatch(
            showToast({
              message: "Speaker Attribution Check maximum number of attempts reached, skipping...",
              severity: "warning",
            })
          );

          return { hasResults: true, results: [] };
        }
      }

      if (attributionResults && attributionResults.length > 0) {
        dispatch(incrementSpeakerAttributionLimit());
        dispatch(
          updateSpeakerAttribution({
            validation: attributionResults,
            meta: data.processed_data?.speaker_attribution_meta,
          }),
        );
        callback();

        return {
          hasResults: true,
          results: attributionResults,
        };
      }

      if (attributionResults && attributionResults.length === 0) {
        dispatch(incrementSpeakerAttributionLimit());
        dispatch(
          updateSpeakerAttribution({
            validation: [],
            meta: data.processed_data?.speaker_attribution_meta,
          }),
        );
        callback();
        dispatch(
          showToast({
            message: "All Speaker Attribution Checks resolved!",
            severity: "success",
            autoClose: true,
          }),
        );

        return {
          hasResults: true,
          results: [],
        };
      }

      setTimeout(() => {
        dispatch(pollSpeakerAttributionValidation(payload));
      }, 30 * 1000);

      return { hasResults: false, results: [] };
    } catch (error) {
      console.log("Speaker attribution polling failed, trying again...", error);

      setTimeout(() => {
        dispatch(pollSpeakerAttributionValidation(payload));
      }, 10 * 1000);
    }
  }
);

export default editorSlice.reducer;
